// src/lib/beds24-mapping-lookup.js
// Source de vérité = logements_beds24_mapping (V2 relationnel).
// Cache module-level 5 min (compromis stale vs round-trips).
// Invalidation via clearMappingCache après upsert/delete.
//
// Tests manuels :
//  1) lc1 mappé → row { logement_id:'lc1', beds24_property_id:325559, beds24_room_id:675830, active:true, property_name:... }
//  2) lc2 sans mapping → null
//  3) après applyMapping(lc2,...) côté Beds24SyncPanel + clearMappingCache('lc2') → row fraîche au prochain getBeds24Mapping
//  4) network down → throws Error('mapping_lookup_failed: ...')
//  5) deux appels rapprochés sur même id → 2ᵉ sert le cache (pas de 2ᵉ round-trip Supabase)

import { supabase } from "../supabase"

const TTL_MS = 5 * 60 * 1000
const cache = new Map() // logement_id -> { value: row|null, fetchedAt: number }

export async function getBeds24Mapping(logementId) {
  if (!logementId) return null
  const cached = cache.get(logementId)
  if (cached && (Date.now() - cached.fetchedAt) < TTL_MS) return cached.value

  const { data, error } = await supabase
    .from("logements_beds24_mapping")
    .select("logement_id, beds24_property_id, beds24_room_id, active, property_name")
    .eq("logement_id", logementId)
    .eq("active", true)
    .maybeSingle()
  if (error) throw new Error(`mapping_lookup_failed: ${error.message}`)
  cache.set(logementId, { value: data || null, fetchedAt: Date.now() })
  return data || null
}

// ═══════════════════════════════════════════════════════════════════════════
// Ensemble des logements RÉELLEMENT raccordés à Beds24 (mapping actif).
// ═══════════════════════════════════════════════════════════════════════════
// Ajouté le 2026-08-05 pour l'avertissement de l'éditeur de messages : l'écran
// laisse cocher les 11 logements alors que seuls ceux qui ont un mapping actif
// reçoivent des réservations de plateforme — donc peuvent déclencher un envoi.
// LECTURE SEULE : ne change rien au moteur d'envoi, sert uniquement à prévenir.
let allActiveCache = null // { value: Set<string>, fetchedAt: number }

export async function getActiveMappedLogementIds() {
  if (allActiveCache && (Date.now() - allActiveCache.fetchedAt) < TTL_MS) {
    return allActiveCache.value
  }
  const { data, error } = await supabase
    .from("logements_beds24_mapping")
    .select("logement_id")
    .eq("active", true)
  if (error) throw new Error(`mapping_lookup_failed: ${error.message}`)
  const ids = new Set((data || []).map(r => r.logement_id).filter(Boolean))
  allActiveCache = { value: ids, fetchedAt: Date.now() }
  return ids
}

export function clearMappingCache(logementIdOrIds) {
  allActiveCache = null
  if (!logementIdOrIds) { cache.clear(); return }
  if (Array.isArray(logementIdOrIds)) {
    logementIdOrIds.forEach(id => cache.delete(id))
  } else {
    cache.delete(logementIdOrIds)
  }
}
